import { Skeleton, Typography } from '@neo4j-ndl/react';
import { MagnifyingGlassIconOutline } from '@neo4j-ndl/react/icons';
import type { Organization } from '@nx/state';
import { isNonEmptyString } from '@nx/stdlib';
import { Grid } from '@nx/ui';
import { useMemo } from 'react';

import { OrgCard, orgCardCols } from './orgs-card';

type OrgListProps = {
  orgs: Organization[];
  filter: string;
  isLoading: boolean;
};

export const OrgList = ({ orgs, filter, isLoading }: OrgListProps) => {
  const normalizedFilter = filter.trim().toLocaleLowerCase();

  const filteredOrgs = useMemo(() => {
    if (isNonEmptyString(normalizedFilter)) {
      return orgs.filter(({ displayName, name, id }) =>
        [displayName, name, id].some((str) => str.toLocaleLowerCase().includes(normalizedFilter)),
      );
    }
    return orgs;
  }, [normalizedFilter, orgs]);

  if (isLoading) {
    return (
      <Grid>
        {Array.from({ length: 1 }).map((_, index) => (
          <Skeleton
            key={index}
            as={Grid.Item}
            className="rounded-1xl h-[110px] w-full"
            htmlAttributes={{
              cols: orgCardCols,
            }}
          />
        ))}
      </Grid>
    );
  }

  return (
    <Grid>
      {filteredOrgs.length === 0 && (
        <Grid.Item
          cols={{ min: 12 }}
          className="border-palette-neutral-border-weak rounded-1xl bg-palette-neutral-bg-weak flex h-36 w-full flex-col items-center justify-center gap-1 border p-6"
        >
          <MagnifyingGlassIconOutline className="text-palette-neutral-icon size-12" />
          <Typography variant="body-medium">No results found</Typography>
        </Grid.Item>
      )}
      {filteredOrgs.length > 0 && filteredOrgs.map((org) => <OrgCard key={org.id} org={org} />)}
    </Grid>
  );
};
